/* Dynamic table of contents for article pages.
   Builds the TOC from h2/h3/h4 headings in the article body,
   smooth-scrolls on click, and scroll-spies the active section. */

class ArticleTOC extends HTMLElement {
  connectedCallback() {
    const bodySelector = this.dataset.body || '.article-body';
    const body = document.querySelector(bodySelector);
    if (!body) {
      this.hidden = true;
      return;
    }

    const headings = body.querySelectorAll('h2, h3, h4');
    if (headings.length < 2) {
      this.hidden = true;
      return;
    }

    this.headings = Array.from(headings);
    this.list = this.querySelector('.article-toc__list');
    this.mobileToggle = this.querySelector('.article-toc__mobile-toggle');
    this.mobileContent = this.querySelector('.article-toc__mobile-content');

    this.buildList();
    this.bindEvents();
    this.observeHeadings();
  }

  buildList() {
    if (!this.list) return;
    this.list.innerHTML = '';
    this.headings.forEach((heading, i) => {
      if (!heading.id) {
        heading.id = this.slugify(heading.textContent) || `heading-${i}`;
      }
      const level = heading.tagName.toLowerCase();
      const li = document.createElement('li');
      li.className = `article-toc__item article-toc__item--${level}`;
      const a = document.createElement('a');
      a.href = `#${heading.id}`;
      a.textContent = heading.textContent;
      a.className = 'article-toc__link';
      a.dataset.target = heading.id;
      li.appendChild(a);
      this.list.appendChild(li);
    });
  }

  slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.article-toc__link');
      if (!link) return;
      e.preventDefault();
      const id = link.dataset.target;
      const target = document.getElementById(id);
      if (!target) return;
      const headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height') || 80,
        10
      );
      const y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16;
      window.scrollTo({ top: y, behavior: 'smooth' });
      history.replaceState(null, '', `#${id}`);
      if (this.mobileContent && this.mobileContent.hasAttribute('open')) {
        this.mobileContent.removeAttribute('open');
      }
    });
  }

  observeHeadings() {
    const options = {
      rootMargin: '-20% 0px -65% 0px',
      threshold: 0,
    };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.setActive(entry.target.id);
      });
    }, options);
    this.headings.forEach((h) => this.observer.observe(h));
  }

  setActive(id) {
    this.querySelectorAll('.article-toc__link').forEach((link) => {
      link.classList.toggle('article-toc__link--active', link.dataset.target === id);
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('article-toc', ArticleTOC);
